'use client';
import React, { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import { useTranslation } from '@/i18n';
import { usePlatformSettings } from '@/contexts/PlatformSettingsContext';

interface PublicSettings {
  platform_name: string;
  support_email: string;
  support_phone: string;
  company_address: string;
  website_url: string;
  logo_url: string;
  platform_tax_registration_number?: string | null;
  facebook_url: string;
  instagram_url: string;
  linkedin_url: string;
  twitter_url: string;
  youtube_url: string;
  whatsapp_number: string;
}

type FooterColumn = { id: string; title: string; sort_order: number; is_enabled: boolean };
type FooterLink = {
  id: string;
  column_id: string | null;
  label: string;
  item_type: string;
  href: string;
  sort_order: number;
  is_enabled: boolean;
  opens_new_tab: boolean;
};
type FooterContent = { short_description: string | null; copyright_text: string | null };

// Social icon SVGs (inline, no extra package needed)
function FacebookIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
    </svg>
  );
}
function InstagramIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" />
    </svg>
  );
}
function LinkedInIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}
function TwitterXIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
function YouTubeIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  );
}
function WhatsAppIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

export default function LandingFooter() {
  const { t, locale } = useTranslation();
  const [settings, setSettings] = useState<PublicSettings | null>(null);
  const [footerContent, setFooterContent] = useState<FooterContent | null>(null);
  const [columns, setColumns] = useState<FooterColumn[]>([]);
  const [links, setLinks] = useState<FooterLink[]>([]);
  const { platformName, companyName } = usePlatformSettings();

  const renderCopyrightText = (text: string) => {
    const marker = 'MCS Consultancy';
    const idx = text.indexOf(marker);
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const after = text.slice(idx + marker.length);
    return (
      <>
        {before}
        <a
          href="https://www.mc1services.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold text-accent hover:text-accent/90 transition-colors"
        >
          {marker}
        </a>
        {after}
      </>
    );
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('platform_public_settings')
          .select(
            'platform_name, support_email, support_phone, company_address, website_url, logo_url, platform_tax_registration_number, facebook_url, instagram_url, linkedin_url, twitter_url, youtube_url, whatsapp_number'
          )
          .limit(1)
          .maybeSingle();
        if (data) setSettings(data as PublicSettings);
      } catch {
        // silently fail — footer uses defaults
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const supabase = createClient();
        const [contentRes, columnsRes, linksRes] = await Promise.all([
          supabase
            .from('platform_footer_content')
            .select('short_description, copyright_text')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle(),
          supabase
            .from('platform_footer_columns')
            .select('id, title, sort_order, is_enabled')
            .eq('is_enabled', true)
            .order('sort_order', { ascending: true })
            .limit(20),
          supabase
            .from('platform_footer_links')
            .select('id, column_id, label, item_type, href, sort_order, is_enabled, opens_new_tab')
            .eq('is_enabled', true)
            .order('sort_order', { ascending: true })
            .limit(100),
        ]);

        if (!contentRes.error && contentRes.data) setFooterContent(contentRes.data as any);
        if (!columnsRes.error && Array.isArray(columnsRes.data)) {
          setColumns(
            (columnsRes.data as any[]).map((c) => ({
              id: String(c.id),
              title: String(c.title || ''),
              sort_order: Number(c.sort_order || 0),
              is_enabled: c.is_enabled !== false,
            }))
          );
        }
        if (!linksRes.error && Array.isArray(linksRes.data)) {
          setLinks(
            (linksRes.data as any[]).map((l) => ({
              id: String(l.id),
              column_id: l.column_id ? String(l.column_id) : null,
              label: String(l.label || ''),
              item_type: String(l.item_type || 'internal'),
              href: String(l.href || ''),
              sort_order: Number(l.sort_order || 0),
              is_enabled: l.is_enabled !== false,
              opens_new_tab: l.opens_new_tab === true,
            }))
          );
        }
      } catch {
        // ignore — fallback used
      }
    };
    fetchFooter();
  }, []);

  const taxRegistrationNumber = String(settings?.platform_tax_registration_number || '').trim();

  const socialLinks = [
    settings?.facebook_url
      ? {
          href: settings.facebook_url,
          label:
            t('publicWebsite:footer.socialLabels.facebook') ||
            (locale === 'ar' ? 'فيسبوك' : 'Facebook'),
          Icon: FacebookIcon,
        }
      : null,
    settings?.instagram_url
      ? {
          href: settings.instagram_url,
          label: t('publicWebsite:footer.socialLabels.instagram') || 'Instagram',
          Icon: InstagramIcon,
        }
      : null,
    settings?.linkedin_url
      ? {
          href: settings.linkedin_url,
          label:
            t('publicWebsite:footer.socialLabels.linkedin') ||
            (locale === 'ar' ? 'لينكدإن' : 'LinkedIn'),
          Icon: LinkedInIcon,
        }
      : null,
    settings?.twitter_url
      ? {
          href: settings.twitter_url,
          label:
            t('publicWebsite:footer.socialLabels.twitter') ||
            (locale === 'ar' ? 'إكس / تويتر' : 'X / Twitter'),
          Icon: TwitterXIcon,
        }
      : null,
    settings?.youtube_url
      ? {
          href: settings.youtube_url,
          label:
            t('publicWebsite:footer.socialLabels.youtube') ||
            (locale === 'ar' ? 'يوتيوب' : 'YouTube'),
          Icon: YouTubeIcon,
        }
      : null,
    settings?.whatsapp_number
      ? {
          href: `https://wa.me/${settings.whatsapp_number.replace(/\D/g, '')}`,
          label:
            t('publicWebsite:footer.socialLabels.whatsapp') ||
            (locale === 'ar' ? 'واتساب' : 'WhatsApp'),
          Icon: WhatsAppIcon,
        }
      : null,
  ].filter(Boolean) as { href: string; label: string; Icon: React.FC }[];

  const fallbackColumns: Array<{
    title: string;
    links: Array<{
      id: string;
      label: string;
      href: string;
      item_type: string;
      opens_new_tab: boolean;
    }>;
  }> = [
    {
      title:
        t('publicWebsite:footer.fallbackColumns.product') ||
        (locale === 'ar' ? 'المنتج' : 'Product'),
      links: [
        {
          id: 'fl-features',
          label:
            t('publicWebsite:footer.fallbackColumns.features') ||
            (locale === 'ar' ? 'الميزات' : 'Features'),
          href: '#features',
          item_type: 'anchor',
          opens_new_tab: false,
        },
        {
          id: 'fl-pricing',
          label:
            t('publicWebsite:footer.fallbackColumns.pricing') ||
            (locale === 'ar' ? 'الأسعار' : 'Pricing'),
          href: '#pricing',
          item_type: 'anchor',
          opens_new_tab: false,
        },
        {
          id: 'fl-faqs',
          label:
            t('publicWebsite:footer.fallbackColumns.faqs') ||
            (locale === 'ar' ? 'الأسئلة الشائعة' : 'FAQs'),
          href: '/faqs',
          item_type: 'internal',
          opens_new_tab: false,
        },
        {
          id: 'fl-desktop-app',
          label:
            t('publicWebsite:footer.fallbackColumns.desktopApp') ||
            (locale === 'ar' ? 'تطبيق سطح المكتب' : 'Desktop App'),
          href: '/desktop-app',
          item_type: 'internal',
          opens_new_tab: false,
        },
      ],
    },
    {
      title:
        t('publicWebsite:footer.fallbackColumns.company') ||
        (locale === 'ar' ? 'الشركة' : 'Company'),
      links: [
        {
          id: 'fl-about',
          label:
            t('publicWebsite:footer.fallbackColumns.about') ||
            (locale === 'ar' ? 'نبذة عنا' : 'About'),
          href: '#about',
          item_type: 'anchor',
          opens_new_tab: false,
        },
        {
          id: 'fl-contact',
          label:
            t('publicWebsite:footer.fallbackColumns.contact') ||
            (locale === 'ar' ? 'اتصل بنا' : 'Contact'),
          href: '/contact',
          item_type: 'internal',
          opens_new_tab: false,
        },
      ],
    },
    {
      title:
        t('publicWebsite:footer.fallbackColumns.legal') || (locale === 'ar' ? 'القانوني' : 'Legal'),
      links: [
        {
          id: 'fl-privacy',
          label:
            t('publicWebsite:footer.fallbackColumns.privacyPolicy') ||
            (locale === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy'),
          href: '/privacy-policy',
          item_type: 'internal',
          opens_new_tab: false,
        },
        {
          id: 'fl-terms',
          label:
            t('publicWebsite:footer.fallbackColumns.termsOfService') ||
            (locale === 'ar' ? 'شروط الخدمة' : 'Terms of Service'),
          href: '/terms-of-service',
          item_type: 'internal',
          opens_new_tab: false,
        },
        {
          id: 'fl-dpa',
          label:
            t('publicWebsite:footer.fallbackColumns.dpa') ||
            (locale === 'ar' ? 'اتفاقية معالجة البيانات' : 'Data Processing Agreement'),
          href: '/data-processing-agreement',
          item_type: 'internal',
          opens_new_tab: false,
        },
      ],
    },
  ];

  const resolveHref = (itemType: string, href: string) => {
    const safeHref = String(href || '');
    if (itemType === 'anchor' && safeHref.startsWith('#')) return `/${safeHref}`;
    return safeHref;
  };

  const effectiveColumns =
    columns.length > 0
      ? columns.map((c) => ({
          title: c.title,
          links: links
            .filter((l) => l.column_id === c.id)
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((l) => ({
              id: l.id,
              label: l.label,
              href: l.href,
              item_type: l.item_type,
              opens_new_tab: l.opens_new_tab,
            })),
        }))
      : fallbackColumns;

  const shortDescription =
    footerContent?.short_description ||
    t('publicWebsite:footer.fallbackDescription') ||
    'The complete SaaS platform for UAE PRO service companies. Manage licences, visas, renewals, cases, and invoices — all in one place.';
  const copyrightText =
    footerContent?.copyright_text || `© 2026 ${platformName}. All rights reserved.`;

  return (
    <footer className="bg-primary text-white py-16 border-t border-white/10">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <AppLogo className="h-8 w-auto" />
              <span className="font-bold text-xl text-white whitespace-nowrap">{platformName}</span>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-xs">{shortDescription}</p>

            {/* Contact info from settings */}
            {(settings?.support_email || settings?.support_phone) && (
              <div className="mt-4 space-y-1">
                {settings?.support_email && (
                  <a
                    href={`mailto:${settings.support_email}`}
                    className="flex items-center gap-2 text-white/60 hover:text-white text-xs transition-colors"
                  >
                    <span>✉</span> {settings.support_email}
                  </a>
                )}
                {settings?.support_phone && (
                  <a
                    href={`tel:${settings.support_phone}`}
                    className="flex items-center gap-2 text-white/60 hover:text-white text-xs transition-colors"
                  >
                    <span>📞</span> {settings.support_phone}
                  </a>
                )}
                {settings?.company_address && (
                  <p className="flex items-center gap-2 text-white/60 text-xs">
                    <span>📍</span> {settings.company_address}
                  </p>
                )}
                {settings?.website_url && (
                  <a
                    href={settings.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-white/60 hover:text-white text-xs transition-colors"
                  >
                    <span>🌐</span> {settings.website_url}
                  </a>
                )}
              </div>
            )}

            {/* Social links */}
            {socialLinks.length > 0 && (
              <div className="mt-4 flex items-center gap-3">
                {socialLinks.map(({ href, label, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            )}

            <div className="mt-5 flex items-center gap-1.5 text-white/40 text-xs">
              <span className="w-2 h-2 rounded-full bg-success" />
              {t('publicWebsite:footer.allSystemsOperational') ||
                (locale === 'ar' ? 'جميع الأنظمة تعمل بشكل طبيعي' : 'All systems operational')}
            </div>
          </div>

          {effectiveColumns.slice(0, 3).map((col) => (
            <div key={`footer-col-${col.title}`}>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">
                {col.title}
              </p>
              <ul className="space-y-2.5">
                {col.links.map((l) => {
                  const href = resolveHref(l.item_type, l.href);
                  return (
                    <li key={l.id}>
                      <a
                        href={href}
                        target={
                          l.item_type === 'external' || l.opens_new_tab ? '_blank' : undefined
                        }
                        rel={
                          l.item_type === 'external' || l.opens_new_tab
                            ? 'noopener noreferrer'
                            : undefined
                        }
                        className="text-sm text-white/60 hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {effectiveColumns.length >= 4 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">
                {effectiveColumns[3].title}
              </p>
              <ul className="space-y-2.5">
                {effectiveColumns[3].links.map((l) => {
                  const href = resolveHref(l.item_type, l.href);
                  return (
                    <li key={l.id}>
                      <a
                        href={href}
                        target={
                          l.item_type === 'external' || l.opens_new_tab ? '_blank' : undefined
                        }
                        rel={
                          l.item_type === 'external' || l.opens_new_tab
                            ? 'noopener noreferrer'
                            : undefined
                        }
                        className="text-sm text-white/60 hover:text-white transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">{renderCopyrightText(copyrightText)}</p>
          <div className="flex items-center gap-4">
            <span className="text-white/40 text-xs">
              {locale === 'ar' ? `مدعوم من ${companyName}` : `Powered by ${companyName}`}
            </span>
            <span className="text-white/40 text-xs">
              {t('publicWebsite:footer.madeForUAE') ||
                (locale === 'ar'
                  ? '🇦🇪 مصمم لدولة الإمارات العربية المتحدة'
                  : '🇦🇪 Made for the UAE')}
            </span>
            {taxRegistrationNumber ? (
              <>
                <span className="text-white/20 text-xs">|</span>
                <span className="text-white/40 text-xs">
                  {(
                    t('publicWebsite:footer.vatRegNo') ||
                    (locale === 'ar'
                      ? 'رقم تسجيل ضريبة القيمة المضافة: {number}'
                      : 'VAT Reg. No: {number}')
                  ).replace('{number}', taxRegistrationNumber)}
                </span>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </footer>
  );
}
